import {
  POST_TYPE_FIELD_KEY,
  type CardPrintTemplate,
  type DistrictMode,
} from '@citizen-walk/shared-types';
import type { CardPrintField } from './cardLayout';
import type { CardPrintPost } from './useCardPrintPosts';

/**
 * テンプレート (M7 cardPrintTemplates) 不要の「組み込みフィールドカタログ」。
 *
 * ## 背景 (なぜ必要か)
 * v2 はカード印刷項目を M7 `cardPrintTemplates.fields` 駆動に再設計したが、その結果
 * **地区 mode に一致するテンプレートが無いと項目を 1 つも追加できない** 回帰が生じた
 * (old2 はテンプレ非依存で固定カタログ + 動的 extra を提供していた)。
 *
 * 本モジュールは old2 `cardPrint/constants.ts` の `ALL_FIELDS` / `FIELD_LABELS` /
 * `DEFAULT_FIELDS_BY_MODE` を v2 の構造化データ (post / extraFields / aiAnalysis / author)
 * へ移植し、**テンプレートが無くても** 地区ごとに post データ・AI 解析結果・地区固有
 * (extraFields) から自由にフィールドを選んでレイアウトできるようにする。
 *
 * ## old2 → v2 の対応
 *   - `title` / `comment` → source 'post'
 *   - `category` → source 'extraFields' (admin DataSettings 由来 label、バッジ描画)
 *   - `authorNickname` → source 'author'
 *   - `agreeCount` → source 'post' (数値 → 文字列)
 *   - `createdAt` / `location` / `aiSummary` / `aiTopCategory` / `aiEmotion`
 *     → `fieldPath` 空の computed field (cardLayout.extractComputedFieldValue が key で計算)
 *   - `qrCode` → overlay 描画専用 (テキストには出さない)
 *   - `adminNote` → jobConfig.makeAdminNoteField() (本カタログには含めない)
 *
 * `subType` / `aiRiskLevel` は v2 データモデルに対応物が無いため移植対象外
 * (旧 `aiRiskLevel` は防災 classifications.assessment へ統合、aiTopCategory でカバー)。
 */

/** computed field の displayOrder 基準。jobFields は編集時に並べ替えるため目安。 */
export const BUILTIN_CARD_FIELDS: CardPrintField[] = [
  { key: 'title', label: 'タイトル', source: 'post', fieldPath: 'title', displayOrder: 0 },
  {
    key: 'category',
    label: 'バッジ（選択式の項目）',
    source: 'extraFields',
    fieldPath: POST_TYPE_FIELD_KEY,
    displayOrder: 1,
  },
  { key: 'comment', label: 'コメント', source: 'post', fieldPath: 'comment', displayOrder: 2 },
  {
    key: 'authorNickname',
    label: '投稿者',
    source: 'author',
    fieldPath: 'authorNickname',
    displayOrder: 3,
  },
  { key: 'createdAt', label: '投稿日', source: 'post', fieldPath: '', displayOrder: 4 },
  { key: 'location', label: '緯度経度', source: 'post', fieldPath: '', displayOrder: 5 },
  { key: 'agreeCount', label: '共感数', source: 'post', fieldPath: 'agreeCount', displayOrder: 6 },
  // セッション名は post.sessionId を sessionNameMap で名前解決する computed field
  // (fieldPath 空。CardPrintPage が読込んだ sessions 一覧から map を構築して渡す)。
  { key: 'sessionName', label: 'セッション名', source: 'post', fieldPath: '', displayOrder: 7 },
  { key: 'aiSummary', label: 'AI概要', source: 'aiAnalysis', fieldPath: '', displayOrder: 8 },
  // 主要カテゴリは ECW で categoryCodes (例 'D-01') を返すため、既定でコード→日本語名変換を ON。
  // bousai/walker は category_name (日本語) を返すのでマップ未ヒット=そのまま (無害)。
  {
    key: 'aiTopCategory',
    label: 'AI主要カテゴリ',
    source: 'aiAnalysis',
    fieldPath: '',
    style: { resolveCodes: true },
    displayOrder: 9,
  },
  { key: 'aiEmotion', label: 'AI感情', source: 'aiAnalysis', fieldPath: '', displayOrder: 10 },
  // AI 生成タイトル (extraFields.aiGeneratedTitle、old2 / user PostDetailModal「投稿概要」と同源)。
  // AI パイプラインが extraFields に書込む文字列。第一級フィールドとして常時追加候補に出す。
  {
    key: 'aiGeneratedTitle',
    label: 'AI生成タイトル',
    source: 'extraFields',
    fieldPath: 'aiGeneratedTitle',
    displayOrder: 11,
  },
  { key: 'qrCode', label: 'QRコード(地図リンク)', source: 'post', fieldPath: '', displayOrder: 12 },
];

/**
 * 組み込みカタログが「プレーンテキストとして」既にカバーする extraFields の fieldPath 集合。
 * discoverExtraFields がこれらを `extra:<key>` として二重に出さないようにするための除外キー。
 * （`category` はバッジ描画専用の組み込みなので除外せず、テキスト版 `extra:category` も併存させる）
 */
export const BUILTIN_EXTRA_FIELD_PATHS: ReadonlySet<string> = new Set(
  BUILTIN_CARD_FIELDS.filter((f) => f.source === 'extraFields' && f.key !== 'category').map(
    (f) => f.fieldPath,
  ),
);

/** key → 組み込みカタログ field の索引 (DEFAULT_FIELDS_BY_MODE の解決用)。 */
const BUILTIN_BY_KEY = new Map(BUILTIN_CARD_FIELDS.map((f) => [f.key, f]));

/**
 * テンプレートが無い地区での既定表示項目 (old2 `DEFAULT_FIELDS_BY_MODE` 踏襲)。
 * v2 に無い field (`aiRiskLevel`) は除外、bousai は aiTopCategory に寄せた。
 *
 * 【廃止予定 fallback】mode キー直引きの既定は組込4モードのみ (モードドリブン化 2026-07-24)。
 * custom mode は ecw 既定に落ちる (buildDefaultJobFields)。本命は M7 CardPrintTemplate を
 * 地区に紐付ける運用で、この既定はテンプレ未設定時の互換 fallback。
 */
export const DEFAULT_FIELD_KEYS_BY_MODE: Record<DistrictMode, string[]> = {
  bousai: ['category', 'title', 'aiTopCategory', 'comment', 'authorNickname'],
  ecw: ['title', 'aiEmotion', 'aiSummary', 'comment', 'agreeCount', 'authorNickname'],
  walker: ['title', 'aiTopCategory', 'aiSummary', 'comment', 'authorNickname'],
  'kids-bousai': ['category', 'title', 'comment', 'authorNickname', 'createdAt'],
};

/**
 * 地区 mode の既定 jobFields (テンプレ無し時の初期項目)。
 * mode 不明時は ecw 既定にフォールバック。
 */
export function buildDefaultJobFields(mode: DistrictMode | null): CardPrintField[] {
  const keys = (mode && DEFAULT_FIELD_KEYS_BY_MODE[mode]) || DEFAULT_FIELD_KEYS_BY_MODE.ecw;
  const out: CardPrintField[] = [];
  let order = 0;
  for (const k of keys) {
    const f = BUILTIN_BY_KEY.get(k);
    if (f) out.push({ ...f, displayOrder: order++ });
  }
  return out;
}

/**
 * aiAnalysis のうち動的発見の対象とする sub-object (フラットな payload を持つもの)。
 * 【廃止予定 fallback を含む】'ecw'/'bousai'/'walker' は legacy 地区 (legacyModeResults) の
 * mode 別 result 用。新規地区は 'generic' のみに結果が入る (モードドリブン化 2026-07-24)。
 */
const AI_DISCOVERY_SUBS = ['ecw', 'generic', 'bousai', 'walker'] as const;

/**
 * 既知 AI キーの日本語ラベル (それ以外は `AI(<sub>): <key>` にフォールバック)。
 * 【廃止予定 fallback を含む】mode-prefixed のラベルは legacy 地区向け。generic キーの
 * ラベルを M3 fields[].label から引く改善は issue 参照 (現状は `AI(generic): <key>` 表示)。
 */
const AI_FIELD_LABELS: Record<string, string> = {
  'ecw.description': 'AI: 概要(ECW)',
  'ecw.emotionText': 'AI: 感情テキスト',
  'ecw.emotions': 'AI: 感情リスト',
  'ecw.emotionReason': 'AI: 感情の理由',
  'ecw.categoryCodes': 'AI: 分類コード',
  'ecw.categoryReason': 'AI: 分類の理由',
  'bousai.description': 'AI: 概要(防災)',
  'bousai.professional_insight': 'AI: 専門的考察(防災)',
  'bousai.reasoning': 'AI: 根拠(防災)',
  'walker.description': 'AI: 概要(ウォーカブル)',
  'walker.professional_insight': 'AI: 専門的考察(ウォーカブル)',
  'walker.walkable_score': 'AI: 歩きやすさスコア',
};

function isPrimitiveOrPrimitiveArray(v: unknown): boolean {
  if (v == null) return false;
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') return true;
  if (Array.isArray(v)) return v.some((x) => x != null && typeof x !== 'object');
  return false;
}

/**
 * 投稿群の `aiAnalysis` から「自由に持ってこられる」AI フィールドを動的発見する。
 *
 * 各 sub-object (ecw / generic / bousai / walker) のフラットな leaf キー
 * (primitive または primitive 配列) を `source='aiAnalysis'`, `fieldPath='<sub>.<key>'`
 * の field 候補に変換。classifications 等の object-array や score object は除外
 * (主要カテゴリは computed `aiTopCategory` でカバー)。fieldPath で dedupe、昇順。
 *
 * key は `ai:<sub>.<key>` で衝突回避 (組み込み aiSummary 等と別物)。
 */
export function discoverAiFields(posts: readonly CardPrintPost[]): CardPrintField[] {
  const seen = new Set<string>();
  const out: CardPrintField[] = [];
  for (const p of posts) {
    const ai = p.aiAnalysis as Record<string, unknown> | null;
    if (!ai) continue;
    for (const sub of AI_DISCOVERY_SUBS) {
      const obj = ai[sub];
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) continue;
      for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
        if (!isPrimitiveOrPrimitiveArray(value)) continue;
        const fieldPath = `${sub}.${key}`;
        if (seen.has(fieldPath)) continue;
        seen.add(fieldPath);
        out.push({
          key: `ai:${fieldPath}`,
          label: AI_FIELD_LABELS[fieldPath] ?? `AI(${sub}): ${key}`,
          source: 'aiAnalysis',
          fieldPath,
          displayOrder: 0,
        });
      }
    }
  }
  return out.sort((a, b) => a.fieldPath.localeCompare(b.fieldPath));
}

/** field がテキスト描画対象外 (overlay / バッジ専用) か。 */
export function isOverlayOnlyField(field: Pick<CardPrintField, 'key'>): boolean {
  return field.key === 'qrCode' || field.key === 'category';
}

/** カテゴリバッジ既定フォントサイズ (pt)。書式未設定時の従来値 (preview / PDF 共通)。 */
export const DEFAULT_BADGE_FONT_PT = 7;
/** バッジフォントサイズの受理範囲 (pt)。範囲外は clamp。 */
export const BADGE_FONT_MIN_PT = 4;
export const BADGE_FONT_MAX_PT = 24;

export interface ResolvedBadgeStyle {
  fontSize: number;
  bold: boolean;
}

/**
 * カテゴリバッジの書式 (フォントサイズ・太字) を field.style から解決する pure helper。
 *
 * カテゴリバッジは overlay 専用 (テキスト行には流れない) ため fieldText の自動調整系とは別に
 * `style.fontSize` / `style.bold` だけを参照する。preview (CSS) と PDF (jspdf) が同じ値を使い
 * 見た目を一致させる。未設定時は従来のハードコード値 (7pt・太字) を維持 (後方互換)。
 *
 * - fontSize: 数値かつ有限・正のときのみ採用し BADGE_FONT_MIN/MAX_PT に clamp、それ以外は既定。
 * - bold: 明示 false のみ非太字、未設定は太字 (従来挙動)。
 */
export function resolveCategoryBadgeStyle(
  field: Pick<CardPrintField, 'style'> | null | undefined,
): ResolvedBadgeStyle {
  const s = field?.style;
  const raw = s?.fontSize;
  const fontSize =
    typeof raw === 'number' && Number.isFinite(raw) && raw > 0
      ? Math.min(BADGE_FONT_MAX_PT, Math.max(BADGE_FONT_MIN_PT, raw))
      : DEFAULT_BADGE_FONT_PT;
  return { fontSize, bold: s?.bold !== false };
}

/** field が QR overlay トグルか。 */
export function isQrField(field: Pick<CardPrintField, 'key'>): boolean {
  return field.key === 'qrCode';
}

/**
 * バッジの「スロット」を表すカード側のフィールド key。fieldPath で対象 postField (既定 投稿タイプ) を指す。
 * カード印刷の保存設定 (fields[].key) に入る識別子で、投稿の入力項目のキーではない。
 */
export const CATEGORY_BADGE_FIELD_KEY = 'category';

/**
 * 表示項目から「カテゴリバッジの対象 postField key」を解決する。
 * バッジのスロットの `fieldPath` (既定 投稿タイプ)。バッジ非表示 (該当なし) は ''。
 */
export function getBadgeFieldPath(fields: readonly CardPrintField[]): string {
  const f = fields.find((x) => x.key === CATEGORY_BADGE_FIELD_KEY);
  if (!f) return '';
  return f.fieldPath || POST_TYPE_FIELD_KEY;
}

/**
 * バッジの対象 postField を差し替える (バッジのスロット key='category' の fieldPath を更新)。
 * 該当フィールドが無ければそのまま返す (バッジ非表示中は対象選択不可)。immutable copy。
 */
export function withBadgeFieldPath(
  fields: readonly CardPrintField[],
  fieldPath: string,
): CardPrintField[] {
  return fields.map((f) =>
    f.key === CATEGORY_BADGE_FIELD_KEY ? { ...f, fieldPath: fieldPath || POST_TYPE_FIELD_KEY } : f,
  );
}

/** template が空 (fields 0 件) または null か。テンプレ無し運用判定に使う。 */
export function isTemplateEmpty(template: Pick<CardPrintTemplate, 'fields'> | null): boolean {
  return !template || template.fields.length === 0;
}
